import type { Notification as DashboardNotification } from '@teemill/dashboard-user';
import { v4 as uuidV4 } from 'uuid';
import type { Component, VNode } from 'vue';
import { markRaw } from 'vue';
import AnnouncementNotification from './components/AnnouncementNotification.vue';
import NudgeNotification from './components/NudgeNotification.vue';
import ShoveNotification from './components/ShoveNotification.vue';
import type { Notification, NotificationOptions } from './queue';
import { queue } from './queue';

export type NotificationContent = string | Component | VNode;

const nudge = (message: NotificationContent, options?: NotificationOptions) => {
  queue.nudge.push({
    props: { message, level: options?.level || 'default', to: options?.to },
    options: {
      id: uuidV4(),
      ...options,
      container: 'nudge',
    },
    component: markRaw(NudgeNotification),
  });
};

const announcement = (message: NotificationContent, options?: NotificationOptions) => {
  queue.announcement.push({
    props: {
      message,
      level: options?.level || 'default',
      to: options?.to,
      actions: options?.actions,
    },
    options: {
      ...options,
      id: options?.id || uuidV4(),
      container: 'announcement',
    },
    component: markRaw(AnnouncementNotification),
  });
};

const shove = (notification: DashboardNotification, options?: NotificationOptions) =>
  queue.shove.push({
    props: { notification },
    options: {
      id: notification.id || uuidV4(),
      ...options,
    },
    component: markRaw(ShoveNotification),
  });

const clear = (name: keyof typeof queue) => {
  queue[name] = [];
};

const remove = (name: keyof typeof queue, notification: Notification | string) => {
  const index =
    typeof notification === 'string'
      ? queue[name].findIndex((item) => item.options?.id === notification)
      : queue[name].indexOf(notification);

  if (index > -1) {
    queue[name].splice(index, 1);
  }
};

const empty = (name: keyof typeof queue) => {
  return queue[name].length === 0;
};

const makeReadonly = <T>(value: T): Readonly<T> => value;

export const useNotifications = () => {
  return {
    nudge,
    announcement,
    clear,
    remove,
    shove,
    empty,
    queue: (name: keyof typeof queue) => makeReadonly(queue[name]),
  };
};
